/**
 * ============================================================
 * App.js — Main Front-End Router + Auth Wrapper
 * ============================================================
 *
 * This file defines the entire front-end routing structure for:
 * -> Public-facing pages (Home, People, Projects, Publications)
 * -> Individual person and project detail pages
 * -> Admin dashboard routes (with JWT authentication guard)
 * 
 * Admin protection is handled by:
 *   <RequireAuth> (checks presence of token only)
 **/

import React from "react";
import "./App.css";

// React Router
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Pages — Public
import Home from "./pages/Home";
import People from "./pages/People";
import Projects from "./pages/Projects";
import Publications from "./pages/Publications";
import Person from "./pages/Person";
import Project from "./pages/Project";

// Pages - Admin
import AdminLogin from "./admin/pages/AdminLogin";
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminUserForm from "./admin/pages/AdminUserForm";
import AdminPeople from "./admin/pages/AdminPeople";
import AdminPersonForm from "./admin/pages/AdminPersonForm";
import AdminProjects from "./admin/pages/AdminProjects";
import AdminProjectForm from "./admin/pages/AdminProjectForm";
import AdminPubs from "./admin/pages/AdminPubs";
import AdminPubForm from "./admin/pages/AdminPubForm";


// ----------------------------------------------------------
// Auth Wrapper
// ----------------------------------------------------------
// Wrap any admin page with <RequireAuth> to protect it.
// If no JWT token exists, redirect to /admin (login page)
function RequireAuth({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/admin" replace />;
}


// ------------------------------------------------------------
// Main App Component
// ------------------------------------------------------------
function App() {
  return (
    <>
      {/* ----- JSX — APPLICATION ROUTES ----- */}
      <Routes>

        {/* ----- PUBLIC ROUTES ----- */}
        <Route path="/" element={<Home />} />
        <Route path="/people" element={<People />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/publications" element={<Publications />} />
        <Route path="/people/:id" element={<Person />} />
        <Route path="/projects/:id" element={<Project />} />
          
        {/* ----- ADMIN LOGIN (No Auth Needed) ----- */}
        <Route path="/admin" element={<AdminLogin />} />

        {/* ----- ADMIN DASHBOARD ----- */}
        <Route
          path="/admin/dashboard"
          element={
            <RequireAuth><AdminDashboard /></RequireAuth>
          }
        />
          
        {/* ----- ADMIN PEOPLE ----- */}
        <Route
          path="/admin/people"
          element={
            <RequireAuth><AdminPeople /></RequireAuth>
          }
        />
        <Route
          path="/admin/people/:id"
          element={
            <RequireAuth><AdminPersonForm /></RequireAuth>
          }
        />

        {/* ----- ADMIN PROJECTS ----- */}
        <Route
          path="/admin/projects"
          element={
            <RequireAuth><AdminProjects /></RequireAuth>
          }
          />
        <Route
          path="/admin/projects/:id"
          element={
            <RequireAuth><AdminProjectForm /></RequireAuth>
          }
        />

        {/* ----- ADMIN PUBLICATIONS ----- */}
        <Route
          path="/admin/publications"
          element={
            <RequireAuth><AdminPubs /></RequireAuth>
          }
        />
        <Route
          path="/admin/publications/:id"
          element={
            <RequireAuth><AdminPubForm /></RequireAuth>
          }
        />

        {/* ----- ADMIN USERS ----- */}
        <Route
          path="/admin/users"
          element={
            <RequireAuth><AdminUsers /></RequireAuth>
          }
        />
        <Route
          path="/admin/users/edit/:id"
          element={
            <RequireAuth><AdminUserForm /></RequireAuth>
          }
        />
        <Route
          path="/admin/users/new"
          element={
            <RequireAuth><AdminUserForm /></RequireAuth>
          }
        />

        {/* ----- ERROR PAGES ----- */}
        <Route
          path="/404"
          element={
            <center>
              <h1 style={{ color: "#000", fontSize: "40px" }}>404</h1>
            </center>
          }
        />

        {/* ----- CATCH-ALL REDIRECT ----- */}
        <Route path="*" element={<Navigate to="/404" replace />} />

      </Routes>
    </>
  );
}

export default App;